import * as Sentry from "@sentry/vue";
import store from '@/store'
import { redactor } from '@/lib/redactor'

// The real DSN never ships to the browser. Envelopes go to the authenticated
// Rails tunnel, which rewrites this placeholder with the DSN from its ENV and
// forwards to Sentry ingest. Must match V1::SentryTunnelController::PLACEHOLDER_DSN.
export const PLACEHOLDER_DSN = 'https://placeholder@placeholder.invalid/0';

// breadcrumbs include navigation, xhr, console logs, user clicks and many others
// we're looking only for xhr, fetch and navigation events
const processBreadcrumb = (breadcrumb) => {
  if (['xhr', 'fetch'].includes(breadcrumb.category) && breadcrumb.data?.url) {
    breadcrumb.data.url = redactor.maskUrl(breadcrumb.data.url);
  } else if (breadcrumb.category === 'navigation') {
    if (breadcrumb.data?.from) {
      breadcrumb.data.from = redactor.maskUrl(breadcrumb.data.from);
    }
    if (breadcrumb.data?.to) {
      breadcrumb.data.to = redactor.maskUrl(breadcrumb.data.to);
    }
  }
  return breadcrumb;
};

// Sends envelopes through the Rails tunnel with the user's credentials, read at
// send time because the Auth0 token rotates. Pre-auth (no token yet) there is
// no way to authenticate, so drop the event with a synthetic 204 — a real
// error response would put the SDK into a retry loop.
const tunnelFetch = (url, options) => {
  if (!store.state.token) {
    return Promise.resolve(new Response(null, { status: 204 }));
  }

  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${store.state.token}`,
      'Session-Id': store.state.uniqueSessionId
    }
  });
};

export const initSentry = (app) => {
  Sentry.init({
    app,
    dsn: PLACEHOLDER_DSN,
    tunnel: `${import.meta.env.VITE_API_BASE_URL}/v1/sentry/envelope`,
    transport: (options) => Sentry.makeFetchTransport(options, tunnelFetch),
    environment: import.meta.env.VITE_ENVIRONMENT_NAME,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // on by default, it attaches the erroring component's props - patient objects included
    attachProps: false,

    beforeBreadcrumb(breadcrumb) {
      return processBreadcrumb(breadcrumb);
    },

    beforeSend(event) {
      if (event?.request?.url) {
        event.request.url = redactor.maskUrl(event.request.url);
      }
      if (event?.request?.headers?.Referer) {
        event.request.headers.Referer = redactor.maskUrl(event.request.headers.Referer);
      }

      if (event?.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(processBreadcrumb);
      }

      return event;
    }
  });
};
